import { Modal } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { useAuth0 } from "@auth0/auth0-react";
import {
  providerProfileState,
  providerEmailNotificationFrequency,
} from "../../recoil/atoms";
import {
  NotificationFrequencyResponse,
  UserProviderProfile,
} from "../../recoil/types";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { useLoginScreen } from "../../utils/hooks/useLoginScreen";
import { PasswordModalWrapper, PasswordModalTitle } from "../Grid/Grid.styles";
import { Button } from "../shared/Button/Button";

import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import AccountDetails from "./AccountDetails";
import EmailNotifications from "./EmailNotifications";
import MfaSettings from "./MfaSettings";
import {
  MyProfileContainer,
  LogoutOutWrapper,
  MyProfileResetPasswordContainer,
  UpdatePasswordButtons,
  UpdatePasswordButton,
} from "./Profile.styles";
import ProfileDetails from "./ProfileDetails";
import ProfileLoadingSkeleton from "./ProfileLoadingSkeleton";
import { ProfileResetPassword } from "./ProfileResetPassword";
import {
  GetEmailNotifications,
  GetProfile,
} from "./Services/UpdateProfileInfoService";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { ResetDemoReqs, ResetOnlineOrders } from "../Grid/Services/GridService";
import {
  isProviderUniversalLoginConfigured,
  startProviderUniversalLogout,
} from "../../auth/providerUniversalLogin";

const Profile = (): React.ReactElement => {
  useLoginScreen();
  const { auth, setLoggedOut } = useAuthenticationHook();
  const { isAuthenticated: isAuth0Authenticated, logout } = useAuth0();
  const [profileInfo, setProfile] = useRecoilState(providerProfileState);
  const [notificationFrequency, setNotificationFrequency] = useRecoilState(
    providerEmailNotificationFrequency,
  );
  const [resetPassword, setResetPassword] = useState(false);
  const { enabled: mfaFeatureEnabled, loading: mfaFlagLoading } =
    useBooleanFeatureFlag(FeatureFlags.ProviderMfa);
  const { enabled: providerCfcEnabled } = useBooleanFeatureFlag(
    FeatureFlags.ProviderCfc,
  );
  const canCreateElectronicOrders =
    auth !== undefined &&
    auth.user_type !== "sales_rep" &&
    auth.is_electronic_ordering_enabled;
  const canShowProviderCfcNotifications =
    providerCfcEnabled && canCreateElectronicOrders;
  const showMfaSettings = mfaFeatureEnabled && !mfaFlagLoading;

  useEffect(() => {
    const getFullProfileInfo = async () => {
      if (auth?.token && !profileInfo) {
        const profileResponse = await GetProfile(auth!.token, setLoggedOut);
        if (profileResponse?.ok) {
          setProfile((await profileResponse.json()) as UserProviderProfile);
        }
      }
    };

    getFullProfileInfo();
  }, [auth?.token]);

  const logoutProviderSession = () => {
    setLoggedOut(false);

    if (isProviderUniversalLoginConfigured() && isAuth0Authenticated) {
      startProviderUniversalLogout(logout);
    }
  };

  useEffect(() => {
    const getNotificationFrequency = async () => {
      if (auth?.token && !notificationFrequency) {
        const notificationResponse = await GetEmailNotifications(
          auth.token,
          setLoggedOut,
        );
        if (notificationResponse?.ok) {
          setNotificationFrequency(
            (await notificationResponse.json()) as NotificationFrequencyResponse,
          );
        }
      }
    };

    getNotificationFrequency();
  }, [auth?.token]);

  const getComponents = () => {
    if (resetPassword) {
      return (
        <ResetPasswordModal
          setMissingInfoModalOpen={setResetPassword}
          missingInfoModalOpen={resetPassword}
        />
      );
    }
    return (
      <MyProfileContainer>
        {profileInfo && notificationFrequency ? (
          <>
            <ProfileDetails profileInfo={profileInfo} />
            <EmailNotifications
              notificationFrequency={notificationFrequency}
              providerCfcEnabled={canShowProviderCfcNotifications}
            />
            <AccountDetails profileInfo={profileInfo} />
            {showMfaSettings && <MfaSettings />}
            {profileInfo.profile.is_demo_user && (
              <>
                <br />
                <UpdatePasswordButton>
                  <Button
                    testId="resetReqsBtn"
                    label="Reset Reqs"
                    size="small"
                    onClick={async () => {
                      try {
                        await ResetDemoReqs(auth!.token, setLoggedOut);
                        // Force a full page reload after the promise resolves
                        window.location.reload();
                      } catch (error) {
                        //
                      }
                    }}
                  />
                </UpdatePasswordButton>
                <p>
                  Resets all release-to-patient, download, and add-on state.
                </p>
                <br />
                <UpdatePasswordButton>
                  <Button
                    testId="resetOnlineOrdersBtn"
                    label="Reset Online Orders"
                    size="small"
                    onClick={async () => {
                      try {
                        await ResetOnlineOrders(auth!.token, setLoggedOut);
                        // Force a full page reload after the promise resolves
                        window.location.reload();
                      } catch (error) {
                        //
                      }
                    }}
                  />
                </UpdatePasswordButton>
                <p>
                  Deletes ALL online orders created by you visible on the online
                  orders tab.
                </p>
              </>
            )}
          </>
        ) : (
          <ProfileLoadingSkeleton
            showCfcNotifications={canShowProviderCfcNotifications}
            showMfaSettings={showMfaSettings}
          />
        )}
        <UpdatePasswordButtons>
          <UpdatePasswordButton>
            <Button
              testId="updatePasswordButton"
              label="Update Password"
              size="small"
              reverse
              onClick={() => setResetPassword(true)}
            />
          </UpdatePasswordButton>
          <LogoutOutWrapper>
            <Button
              testId="logoutButton"
              label="LOG OUT"
              size="medium"
              onClick={logoutProviderSession}
            />
          </LogoutOutWrapper>
        </UpdatePasswordButtons>
      </MyProfileContainer>
    );
  };

  return getComponents();
};

export default Profile;

export const ResetPasswordModal: React.FC<{
  missingInfoModalOpen: boolean;
  setMissingInfoModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ missingInfoModalOpen, setMissingInfoModalOpen }) => {
  const { deviceType } = useDeviceHook();
  return (
    <Modal
      data-testid="resetPasswordModal"
      open={missingInfoModalOpen}
      onClose={setMissingInfoModalOpen}
      style={{
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <PasswordModalWrapper deviceType={deviceType}>
        <PasswordModalTitle deviceType={deviceType}>
          UPDATE PASSWORD
        </PasswordModalTitle>
        <MyProfileResetPasswordContainer>
          <ProfileResetPassword setResetPassword={setMissingInfoModalOpen} />
        </MyProfileResetPasswordContainer>
      </PasswordModalWrapper>
    </Modal>
  );
};
